import { useEffect, useMemo, useState } from "react";
import EntriesTable, { SortingMode, SortDirection } from "../components/EntriesTable/EntriesTable";
import { useEntries, Entry } from "@/shared/contexts/EntriesContext";

type Props = {
  date: Date;
  dateRange?: { from: Date | undefined; to: Date | undefined };
  sortingMode: SortingMode;
  sortDirection: SortDirection;
  onSortByMatter?: () => void;
  onSortByTime?: () => void;
  filterEntries?: <T extends { application?: string }>(entries: T[]) => T[];
  filterByClientMatter?: <T extends { matter_id?: string; client?: string }>(entries: T[]) => T[];
};

export default function HistoryView({ date, dateRange, sortingMode, sortDirection, onSortByMatter, onSortByTime, filterEntries, filterByClientMatter }: Props) {
  const { exportedEntries } = useEntries();
  const [groupedEntries, setGroupedEntries] = useState<Record<string, Entry[]>>({});

  const filteredEntries = useMemo(() => {
    // Filter by single day or date range
    const isInRange = (d: Date) => {
      if (dateRange?.from || dateRange?.to) {
        const start = dateRange.from ? new Date(dateRange.from.getFullYear(), dateRange.from.getMonth(), dateRange.from.getDate(), 0, 0, 0, 0) : null;
        const end = dateRange.to ? new Date(dateRange.to.getFullYear(), dateRange.to.getMonth(), dateRange.to.getDate(), 23, 59, 59, 999) : null;
        if (start && end) return d >= start && d <= end;
        if (start) return d >= start;
        if (end) return d <= end;
        return true;
      }
      return d.getFullYear() === date.getFullYear() && d.getMonth() === date.getMonth() && d.getDate() === date.getDate();
    };
    const dateFiltered = exportedEntries.filter((e) => isInRange(new Date(e.date)));
    const afterAppFilter = filterEntries ? filterEntries(dateFiltered) : dateFiltered;
    return filterByClientMatter ? filterByClientMatter(afterAppFilter) : afterAppFilter;
  }, [exportedEntries, date, dateRange, filterEntries, filterByClientMatter]);

  useEffect(() => {
    const groups: Record<string, Entry[]> = {};
    const mult = sortDirection === "asc" ? 1 : -1;
    if (sortingMode === "matter") {
      const unsorted: Record<string, Entry[]> = {};
      filteredEntries.forEach((row) => {
        const key = row.matter || "Unknown";
        if (!unsorted[key]) unsorted[key] = [];
        unsorted[key].push(row);
      });
      Object.keys(unsorted).sort((a, b) => mult * a.localeCompare(b)).forEach((key) => {
        groups[key] = unsorted[key];
      });
    } else {
      const unsorted: Record<string, Entry[]> = {};
      filteredEntries.forEach((row) => {
        let hourLabel = "";
        if (row.end_time) {
          const dateObj = new Date(row.end_time);
          const hours = dateObj.getUTCHours();
          const period = hours >= 12 ? "PM" : "AM";
          const displayHour = hours % 12 || 12;
          hourLabel = `${displayHour}:00 ${period}`;
        }
        const key = hourLabel || "Unknown";
        if (!unsorted[key]) unsorted[key] = [];
        unsorted[key].push(row);
      });
      const hourLabelTo24Hour = (label: string): number => {
        if (label === "Unknown") return 99;
        const match = label.match(/^(\d+):00 (AM|PM)$/);
        if (!match) return 99;
        const hour = parseInt(match[1], 10);
        const isPM = match[2] === "PM";
        if (hour === 12) return isPM ? 12 : 0;
        return isPM ? hour + 12 : hour;
      };
      Object.keys(unsorted)
        .sort((a, b) => mult * (hourLabelTo24Hour(a) - hourLabelTo24Hour(b)))
        .forEach((key) => { groups[key] = unsorted[key]; });
    }
    setGroupedEntries(groups);
  }, [filteredEntries, sortingMode, sortDirection]);

  return (
    <>
      <EntriesTable
        entries={filteredEntries}
        groupedEntries={groupedEntries}
        columnStorageKey="entries-table:history"
        checkedRows={new Set()}
        selectAllChecked={false}
        selectAllIndeterminate={false}
        onToggleSelectAll={() => {}}
        onToggleRow={() => {}}
        onSortByMatter={onSortByMatter}
        onSortByTime={onSortByTime}
        showCheckboxes={false}
        mode="history"
        sortingMode={sortingMode}
        sortDirection={sortDirection}
      />
    </>
  );
}
